import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface Post {
  id: number;
  slug: string;
  title: string;
  excerpt: string | null;
  cover_image_url: string | null;
  created_at: string;
}

const Section = styled.section`
  padding: 4rem 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;

  h2 {
    font-size: clamp(1.6rem, 3vw, 2.25rem);
    margin: 0;
    color: var(--text-color);
  }
`;

const SeeAll = styled(Link)`
  color: var(--primary-color);
  font-weight: 600;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const FeaturedCard = styled(Link)`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 2rem;
  background: var(--background-light);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  color: inherit;
  text-decoration: none;
  transition: transform 0.3s ease;
  margin-bottom: 3rem;

  &:hover { transform: translateY(-4px); }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedImage = styled.div`
  min-height: 320px;
  background-color: var(--background-dark);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const FeaturedBody = styled.div`
  padding: 2rem 2rem 2rem 0;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }

  .tag {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--primary-color);
    text-transform: uppercase;
    margin-bottom: 0.75rem;
  }

  h3 {
    font-size: 1.75rem;
    margin: 0 0 0.75rem;
    line-height: 1.25;
    color: var(--text-color);
  }

  p {
    color: var(--text-secondary);
    line-height: 1.7;
    margin-bottom: 1.25rem;
  }

  .meta {
    color: var(--text-secondary);
    font-size: 0.85rem;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  background: var(--background-light);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: var(--box-shadow);
  color: inherit;
  text-decoration: none;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease;

  &:hover { transform: translateY(-4px); }
`;

const CardImage = styled.div`
  height: 180px;
  background-color: var(--background-dark);
  overflow: hidden;

  img { width: 100%; height: 100%; object-fit: cover; display: block; }
`;

const CardBody = styled.div`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;

  .meta {
    color: var(--text-secondary);
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }

  h4 {
    font-size: 1.1rem;
    margin: 0 0 0.5rem;
    color: var(--text-color);
    line-height: 1.35;
  }

  p {
    color: var(--text-secondary);
    font-size: 0.95rem;
    line-height: 1.55;
    margin: 0;
  }
`;

const Notice = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
`;

function formatDate(iso: string): string {
  try {
    return new Date(iso.replace(' ', 'T') + 'Z').toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

const LatestPosts: React.FC = () => {
  const [posts, setPosts] = useState<Post[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/posts')
      .then((r) => r.json())
      .then((data) => setPosts(data.posts || []))
      .catch(() => setError('Could not load posts'));
  }, []);

  if (error) return <Section><Notice>{error}</Notice></Section>;
  if (posts === null) return <Section><Notice>Loading latest articles…</Notice></Section>;
  if (posts.length === 0) {
    return (
      <Section>
        <Notice>
          New articles are on the way — check back soon.
        </Notice>
      </Section>
    );
  }

  const [featured, ...rest] = posts;
  const gridPosts = rest.slice(0, 6);

  return (
    <Section>
      <Header>
        <h2>Latest articles</h2>
        <SeeAll to="/blog">Browse all posts →</SeeAll>
      </Header>

      <FeaturedCard to={`/blog/${featured.slug}`}>
        <FeaturedImage>
          {featured.cover_image_url && <img src={featured.cover_image_url} alt={featured.title} />}
        </FeaturedImage>
        <FeaturedBody>
          <div className="tag">Featured</div>
          <h3>{featured.title}</h3>
          {featured.excerpt && <p>{featured.excerpt}</p>}
          <div className="meta">{formatDate(featured.created_at)}</div>
        </FeaturedBody>
      </FeaturedCard>

      {gridPosts.length > 0 && (
        <Grid>
          {gridPosts.map((post) => (
            <Card key={post.id} to={`/blog/${post.slug}`}>
              <CardImage>
                {post.cover_image_url && <img src={post.cover_image_url} alt={post.title} />}
              </CardImage>
              <CardBody>
                <div className="meta">{formatDate(post.created_at)}</div>
                <h4>{post.title}</h4>
                {post.excerpt && <p>{post.excerpt}</p>}
              </CardBody>
            </Card>
          ))}
        </Grid>
      )}
    </Section>
  );
};

export default LatestPosts;
